import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import routesConfig from 'constants/routesConfig.json';

// components
import ApiLoading from 'shared/ApiLoading';
import { TopBar, TopBarMenuItem } from 'topBar/components/TopBar';
import DeleteProfileModal from 'teamProfiles/components/DeleteProfileModal';
import EventGroupCard from 'events/components/EventGroupCard';
import { General } from 'icons/untitledui';

// hooks
import { useProfile } from 'teamProfiles/hooks/teamApi';
import { useScrolledPast } from 'topBar/hooks/useScrolledPast';

// helpers
import { getDateFormatWithYear } from 'shared/helpers/searchUtils';

// types
import type { Event } from 'events/_types';

// styles
import * as PageLayout from 'layout/styles/PageLayout.style';
import * as Styled from 'teamProfiles/styles/EmployeeProfile.style';
import * as Text from 'theme/components/Text.style';
import colours from 'theme/atoms/colours';
import { getInitials } from 'teamProfiles/helpers/profileUtils';

interface EmployeeProfileContentProps {
  profileId: string;
  isDesktopView: boolean;
}

export default function EmployeeProfileContent({
  profileId,
  isDesktopView,
}: EmployeeProfileContentProps): JSX.Element {
  const history = useHistory();
  const { t, i18n } = useTranslation('pageTeam');
  const { data: profile, isLoading: isProfileLoading } = useProfile(profileId);
  const relatedEvents = useMemo(
    () => (profile?.related_events ?? []).filter((e) => e.shift_notes?.length),
    [profile?.related_events]
  );
  const dateFormat = useMemo(
    () => getDateFormatWithYear(i18n.language),
    [i18n.language]
  );

  const openEvent = (event: Event) => {
    history.push(`${routesConfig.EVENT_PAGE}/${event.id}`);
  };
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const titleRef = useRef<HTMLDivElement>(null);
  const isScrolledPast = useScrolledPast(titleRef, profile?.id);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('updated') === 'true') {
      setShowSuccessAlert(true);
      history.replace(`${routesConfig.TEAM_PAGE}/${profileId}`);
      setTimeout(() => setShowSuccessAlert(false), 5000);
    }
  }, [profileId, history]);

  const handleEditDetails = () => {
    history.push(`${routesConfig.TEAM_PAGE}/${profileId}/edit`);
  };

  const handleDeleteProfile = () => {
    setShowDeleteModal(true);
  };

  const menuItems: TopBarMenuItem[] = [
    {
      label: t('employeeProfile.editDetails'),
      onClick: handleEditDetails,
      icon: <General.Edit05 size={20} />,
    },
    {
      label: t('employeeProfile.deleteMember'),
      disabled: profile?.has_notes,
      onClick: handleDeleteProfile,
      icon: <General.Trash01 size={20} />,
    },
  ];

  if (isProfileLoading) return <ApiLoading />;
  if (!profile) return <div>{t('employeeProfile.profileNotFound')}</div>;

  const employmentNumber =
    profile.employee_id || t('employeeProfile.notAvailable');
  const employmentStatus = t(
    `employeeProfile.${profile.employment_status || 'active'}`
  );
  const role = profile.role || t('employeeProfile.notAvailable');
  const departments = (() => {
    const raw = profile.departments ?? [];
    if (!raw.length) return t('employeeProfile.notAvailable');
    const names = raw.map((d) => d.name).filter(Boolean);
    return names.length ? names.join(', ') : t('employeeProfile.notAvailable');
  })();

  return (
    <>
      <TopBar
        onBackClick={() => history.push(routesConfig.TEAM_PAGE)}
        menuItems={menuItems}
        showOnDesktop={isDesktopView}
        showBackButton={!isDesktopView}
        title={profile.full_name}
        showTitle={isScrolledPast}
      />
      <Styled.Container>
        <PageLayout.PageTitleContainer ref={titleRef}>
          <Styled.Header>
            <Styled.Avatar>
              <Text.H5>{getInitials(profile.full_name)}</Text.H5>
            </Styled.Avatar>
            <Text.H1>{profile.full_name}</Text.H1>
          </Styled.Header>
        </PageLayout.PageTitleContainer>

        {showSuccessAlert && (
          <Styled.SuccessAlert>
            <General.CheckCircleBroken
              size={20}
              color={colours.mint[900]}
              style={{ marginRight: '8px' }}
            />
            <Text.P2Semibold>
              {t('employeeProfile.profileUpdated')}
            </Text.P2Semibold>
          </Styled.SuccessAlert>
        )}

        <Styled.DetailsSection>
          <Styled.DetailRow>
            <Text.P2Semibold>{t('employeeProfile.fullName')}</Text.P2Semibold>
            <Text.P2>
              {profile.full_name || t('employeeProfile.notAvailable')}
            </Text.P2>
          </Styled.DetailRow>

          <Styled.Divider />

          <Styled.DetailRow>
            <Text.P2Semibold>{t('employeeProfile.role')}</Text.P2Semibold>
            <Text.P2>{role}</Text.P2>
          </Styled.DetailRow>

          <Styled.Divider />

          {profile.nicknames?.length > 0 && (
            <>
              <Styled.DetailRow>
                <Text.P2Semibold>
                  {t('employeeProfile.nicknames')}
                </Text.P2Semibold>
                <Text.P2>{profile.nicknames?.join(', ')}</Text.P2>
              </Styled.DetailRow>
              <Styled.Divider />
            </>
          )}

          <Styled.DetailRow>
            <Text.P2Semibold>
              {t('employeeProfile.employmentNumber')}
            </Text.P2Semibold>
            <Text.P2>{employmentNumber}</Text.P2>
          </Styled.DetailRow>

          <Styled.Divider />

          <Styled.DetailRow>
            <Text.P2Semibold>
              {t('employeeProfile.employmentStatus')}
            </Text.P2Semibold>
            <Text.P2>{employmentStatus}</Text.P2>
          </Styled.DetailRow>

          <Styled.Divider />

          <Styled.DetailRow>
            <Text.P2Semibold>
              {t('employeeProfile.departments')}
            </Text.P2Semibold>
            <Text.P2>{departments}</Text.P2>
          </Styled.DetailRow>
        </Styled.DetailsSection>

        {relatedEvents.length > 0 && (
          <Styled.EventsSection>
            <Styled.EventsHeader>
              <Text.H5>
                {t('employeeProfile.relatedEvents')} ({relatedEvents.length})
              </Text.H5>
            </Styled.EventsHeader>
            <Styled.EventsList>
              {relatedEvents.map((event) => (
                <EventGroupCard
                  key={event.id}
                  event={event}
                  notes={event.shift_notes ?? []}
                  dateFormat={dateFormat}
                  onEventClick={openEvent}
                  onNoteClick={openEvent}
                />
              ))}
            </Styled.EventsList>
          </Styled.EventsSection>
        )}
      </Styled.Container>

      {!profile?.has_notes && (
        <DeleteProfileModal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          profile={profile}
        />
      )}
    </>
  );
}
